import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
// Removed Search from lucide-react, Input from ui
import { BookOpen, MessageSquareText, Rss, Users, ArrowRight } from "lucide-react"; 
import Navbar from "../../components/layout/Navbar"; // Adjusted path
import { Button } from "../../components/ui/button"; // Adjusted path
import type { TextContent } from "../../types/textContent"; // Adjusted path
import { loadTextContent } from "../../lib/textContent"; // Adjusted path

// Category options for Berättelser
const categories = [
  { id: "all", nameKey: "all", icon: BookOpen },
  { id: "personalStory", nameKey: "personalStory", icon: MessageSquareText },
  { id: "expertInsights", nameKey: "expertInsights", icon: Rss }, // Using Rss for expert insights/news
  { id: "researchUpdates", nameKey: "researchUpdates", icon: Users }, // Using Users as a placeholder, consider a more specific icon
];

const Berattelser = () => {
  // Removed searchQuery state
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [text, setText] = useState<TextContent | null>(null); // Using TextContent, ensure it fits 'berattelser.json'

  useEffect(() => {
    const loadText = async () => {
      const content = await loadTextContent("berattelser"); // Load 'berattelser' content
      setText(content);
    };
    loadText();
  }, []);

  if (!text || !text.articles) return null; // Check for articles specifically

  // Filter articles based on category only
  const filteredArticles = text.articles.items.filter((article) => {
    // Adjust category matching for 'berattelser.json' structure
    const categoryName = text.categories?.[selectedCategory as keyof typeof text.categories];
    const matchesCategory = selectedCategory === "all" || article.category === categoryName;
    
    return matchesCategory;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Hero header */}
          <div className="text-center max-w-3xl mx-auto mb-12 animate-fadeIn">
            <h1 className="text-3xl md:text-4xl font-bold text-minnestig-teal mb-4">
              {text.hero.title}
            </h1>
            <p className="text-lg text-gray-600">
              {text.hero.description}
            </p>
          </div>

          {/* Category filters (Search field removed) */}
          <div className="flex flex-wrap justify-center gap-2 mb-10 animate-slideUp" style={{ animationDelay: "100ms" }}>
            {categories.map((category) => {
              const CategoryIcon = category.icon;
              const categoryDisplayName = text.categories?.[category.nameKey as keyof typeof text.categories] || category.nameKey;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category.id)}
                  className="flex items-center gap-2"
                >
                  <CategoryIcon size={16} />
                  {categoryDisplayName}
                </Button>
              );
            })}
          </div>

          {/* Articles grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-slideUp" style={{ animationDelay: "200ms" }}>
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  // Applied hover:shadow-lg as default (shadow-lg), removed hover effect and transition
                  className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col"
                >
                  <img 
                    src={article.imageUrl || "/placeholder.svg"} 
                    alt={article.title} 
                    className="w-full aspect-square object-cover" // Added image display
                  />
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-minnestig-lavender/30 text-minnestig-purple text-sm font-medium mb-3 self-start">
                      {article.category}
                    </div>
                    <h3 className="text-xl font-semibold text-minnestig-teal mb-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-3 flex-grow"> 
                      {article.description}
                    </p>
                    <div className="mt-auto"> {/* Ensures button is at the bottom */}
                      <Link
                        to={`/berattelser/${article.id}`} // Updated link path
                        className="inline-flex items-center gap-1 text-minnestig-purple font-medium hover:underline"
                      >
                        {text.articles?.readMore} <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-minnestig-beige rounded-xl animate-slideUp" style={{ animationDelay: "200ms" }}>
              <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {text.articles?.noResults.title}
              </h3>
              <p className="text-gray-500 mb-4">
                {text.articles?.noResults.description}
              </p>
              <Button onClick={() => {
                // setSearchQuery(""); // Removed searchQuery reset
                setSelectedCategory("all");
              }}>
                {text.articles?.noResults.resetButton}
              </Button>
            </div>
          )}

          {/* Contact box */}
          {/* Added max-w-3xl mx-auto for consistent width with hero */}
          <div className="mt-16 bg-white rounded-xl shadow-sm overflow-hidden animate-slideUp max-w-3xl mx-auto" style={{ animationDelay: "300ms" }}>
            <div className="p-8 sm:p-10 bg-gradient-to-br from-minnestig-teal to-minnestig-darkpurple text-white">
              {/* Changed layout to text-center and stacked elements */}
              <div className="text-center">
                <div className="mb-6"> {/* Text content wrapper */}
                  <h3 className="text-2xl font-bold mb-2">{text.contact?.title}</h3>
                  <p className="text-white/80 mb-6"> {/* Added margin below paragraph */}
                    {text.contact?.description}
                  </p>
                </div>
                <Link to="/kontakt">
                  <Button size="lg" className="bg-minnestig-purple text-white border-minnestig-purple hover:bg-minnestig-darkpurple hover:border-minnestig-darkpurple">
                    {text.contact?.button}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Berattelser;
